import React from 'react';

import { Box, Skeleton, Typography } from '@mui/material';

import { Field, Form } from '@hrlogics/react-components/form';

import { formatMoney } from '@clearverify/utils';

import {
  ExistingVerificationRequestTotalChargeReason,
  GeneratedReportsMetaDocument,
  GeneratedReportsMetaQueryVariables,
  InboxItemDocument,
  InboxItemQueryVariables,
  InboxItemsScrollableListDocument,
  InboxItemsScrollableListQueryVariables,
  useGetExistingVerificationRequestTotalChargeQuery,
  useStripeGetPaymentIntentForVerificationRequestQuery,
  useStripeUpdatePaymentMethodForVerificationRequestMutation,
  useVerificationRequestPaymentInfoQuery,
  VerificationRequestDocument,
  VerificationRequestPaymentInfoDocument,
  VerificationRequestPaymentInfoQueryVariables,
  VerificationRequestQueryVariables,
} from 'src/api';
import { Button } from 'src/components/common';
import { SelectField } from 'src/components/finalForm';
import { InfoBlock } from 'src/components/shared';
import { useNotification } from 'src/hooks/app';
import { useStripeCustomerPaymentMethods } from 'src/hooks/stripe';
import { useCurrentUserVerifierStaff } from 'src/hooks/verifierStaff';
import type { SelectFieldOption } from 'src/types/components';

import {
  VerificationRequestPaymentTryAgainFormFields,
  VerificationRequestPaymentTryAgainFormValues,
} from './types';

interface VerificationRequestPaymentTryAgainModalFormProps {
  closeModal: () => void;
  switchClosability: (closability: boolean) => void;
  inboxItemId: string;
}

export const VerificationRequestPaymentTryAgainModalForm: React.FC<
  VerificationRequestPaymentTryAgainModalFormProps
> = ({ closeModal, switchClosability, inboxItemId }) => {
  const { spawnNotification } = useNotification();

  const { verifierStaff } = useCurrentUserVerifierStaff();
  const { data: verificationRequestPaymentInfo } = useVerificationRequestPaymentInfoQuery({
    variables: {
      where: { inboxItemId },
    },
  });

  const [needToRedirect, setNeedToRedirect] = React.useState(false);

  const { data: totalChargeData, loading: loadingTotalCharge } =
    useGetExistingVerificationRequestTotalChargeQuery({
      variables: {
        inboxItemId,
        reason: ExistingVerificationRequestTotalChargeReason.TryAgainPayment,
      },
      fetchPolicy: 'no-cache',
    });

  const { paymentMethods, loadingPaymentMethods, defaultPaymentMethodId } =
    useStripeCustomerPaymentMethods(verifierStaff?.stripeCustomerId);

  const { data: paymentIntentData, loading: loadingPaymentIntent } =
    useStripeGetPaymentIntentForVerificationRequestQuery({
      variables: { inboxItemId },
      fetchPolicy: 'no-cache',
    });

  const [updatePaymentMethod] = useStripeUpdatePaymentMethodForVerificationRequestMutation({
    refetchQueries: [
      {
        query: InboxItemDocument,
        variables: { id: inboxItemId } satisfies InboxItemQueryVariables,
      },
      {
        query: InboxItemsScrollableListDocument,
        variables: {
          where: { id: { equals: inboxItemId } },
        } satisfies InboxItemsScrollableListQueryVariables,
      },
      {
        query: VerificationRequestDocument,
        variables: { filter: { inboxItemId } } satisfies VerificationRequestQueryVariables,
      },
      {
        query: VerificationRequestPaymentInfoDocument,
        variables: {
          where: { inboxItemId },
        } satisfies VerificationRequestPaymentInfoQueryVariables,
      },
      {
        query: GeneratedReportsMetaDocument,
        variables: { inboxItemId } satisfies GeneratedReportsMetaQueryVariables,
      },
    ],
  });

  const totalCharge = React.useMemo(
    () => totalChargeData?.getExistingVerificationRequestTotalCharge.totalCharge,
    [totalChargeData?.getExistingVerificationRequestTotalCharge.totalCharge],
  );

  const currentPaymentMethod = React.useMemo(
    () => paymentIntentData?.stripeGetPaymentIntentForVerificationRequest.paymentMethod,
    [paymentIntentData?.stripeGetPaymentIntentForVerificationRequest.paymentMethod],
  );

  const paymentMethodOptions = React.useMemo(
    (): SelectFieldOption[] =>
      paymentMethods.map(paymentMethod => ({
        label: `**** ${paymentMethod.card?.last4 || paymentMethod.us_bank_account?.last4}`,
        value: paymentMethod.id,
      })),
    [paymentMethods],
  );

  const handleSubmit = React.useCallback(
    async (formValues: VerificationRequestPaymentTryAgainFormValues) => {
      switchClosability(false);

      let success = false;

      try {
        const { data } = await updatePaymentMethod({
          variables: {
            inboxItemId,
            paymentMethodId: formValues.paymentMethodId,
          },
        });

        const redirectUrl = data?.stripeUpdatePaymentMethodForVerificationRequest.redirectUrl;
        if (redirectUrl) {
          setNeedToRedirect(true);
          window.location.replace(redirectUrl);
          return;
        }

        success = true;

        spawnNotification(
          'Successfully updated payment method for verification request',
          'success',
        );
      } finally {
        switchClosability(true);
        if (success) {
          closeModal();
        }
      }
    },
    [switchClosability, updatePaymentMethod, inboxItemId, spawnNotification, closeModal],
  );

  return (
    <React.Fragment>
      <InfoBlock.Container gap={1}>
        <InfoBlock.Item
          title="Payment Method"
          value={
            loadingPaymentIntent ? (
              <Skeleton variant="rectangular" height={24} width={100} />
            ) : (
              currentPaymentMethod?.toUpperCase() || 'Unknown payment method'
            )
          }
        />
        <InfoBlock.Item
          title="Payment Status"
          value={<Typography color="error">Declined</Typography>}
        />
        <InfoBlock.Item
          title="Reason"
          value={verificationRequestPaymentInfo?.verificationRequest?.stripeFailedPaymentMessage}
        />
      </InfoBlock.Container>

      <Form<VerificationRequestPaymentTryAgainFormValues>
        onSubmit={handleSubmit}
        containerProps={{ mt: 4 }}
      >
        {({ valid, submitting }) => (
          <React.Fragment>
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={4}>
              <Typography variant="body2" fontSize={20} fontWeight={500}>
                {loadingTotalCharge ? 'Loading...' : `Total charge: ${formatMoney(totalCharge)}`}
              </Typography>

              <Box width={250}>
                {loadingPaymentMethods ? (
                  <Skeleton width="100%" height={60} />
                ) : (
                  <Field
                    name={VerificationRequestPaymentTryAgainFormFields.paymentMethodId}
                    component={SelectField}
                    label="Payment method"
                    options={paymentMethodOptions}
                    fullWidth
                    disabled={submitting}
                    required
                    initialValue={defaultPaymentMethodId}
                  />
                )}
              </Box>
            </Box>

            <Box display="flex" justifyContent="center" gap={4} mt={4}>
              <Button
                sx={{ width: 160 }}
                variant="outlined"
                color="secondary"
                onClick={() => closeModal()}
                disabled={submitting || needToRedirect}
              >
                Cancel
              </Button>
              <Button
                sx={{ width: 160 }}
                type="submit"
                variant="contained"
                color="primary"
                disabled={!valid || submitting || needToRedirect}
                loading={submitting || loadingPaymentIntent || loadingPaymentMethods}
              >
                Submit
              </Button>
            </Box>
          </React.Fragment>
        )}
      </Form>
    </React.Fragment>
  );
};
